import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";

type Proposal = {
  id: string;
  user_id: string;
  type: "passion" | "power";
  category: string | null;
  label: string;
  status: string;
  admin_label: string | null;
  admin_category: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  user_name: string;
  user_avatar: string | null;
};

const initials = (name?: string) => (name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

// Panneau interne, volontairement non listé dans la navigation (accessible seulement en tapant
// /admin) : la protection réelle vient du backend (requireAdmin sur les routes /catalog/admin/*),
// cette page ne fait qu'éviter d'afficher du contenu à un utilisateur non-admin qui tomberait dessus.
export default function Admin() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/catalog/admin/proposals")
      .then(data => {
        if (data?.proposals) setProposals(data.proposals);
        else setError(data?.error || "Erreur lors du chargement des propositions.");
      })
      .catch(() => setError("Erreur de connexion."));
  }, [user]);

  if (!user) return null;
  if (!user.is_admin) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#666" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Accès réservé</div>
        <div style={{ fontSize: 13 }}>Cette page est réservée aux administrateurs OLTODO.</div>
      </div>
    );
  }

  const removeFromList = (id: string) => setProposals(prev => (prev || []).filter(p => p.id !== id));

  const handleApprove = async (id: string, body?: { label: string; category: string }) => {
    setBusyId(id);
    try {
      const data = await api.put(`/catalog/admin/proposals/${id}/approve`, body || {});
      if (data?.message) {
        removeFromList(id);
        setEditingId(null);
      } else {
        alert(data?.error || "Erreur lors de la validation.");
      }
    } catch {
      alert("Erreur de connexion.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id: string) => {
    setBusyId(id);
    try {
      const data = await api.put(`/catalog/admin/proposals/${id}/reject`, {});
      if (data?.message) removeFromList(id);
      else alert(data?.error || "Erreur lors du refus.");
    } catch {
      alert("Erreur de connexion.");
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (p: Proposal) => {
    setEditingId(p.id);
    setEditLabel(p.label);
    setEditCategory(p.category || "");
  };

  const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 16, padding: 20, marginBottom: 12, border: "1px solid #F0F0F0" };
  const primaryBtn: React.CSSProperties = { backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
  const secondaryBtn: React.CSSProperties = { backgroundColor: "white", color: "#0a0a0a", border: "1px solid #E0E0E0", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
  const dangerBtn: React.CSSProperties = { backgroundColor: "white", color: "#c0392b", border: "1px solid #F0D8D5", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
  const input: React.CSSProperties = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 13.5, outline: "none", marginBottom: 8, boxSizing: "border-box" };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 20px 60px" }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Propositions de Passions/Powers</h1>
      <p style={{ fontSize: 13, color: "#999", marginBottom: 24 }}>
        File d'attente des entrées proposées par les membres pour enrichir le catalogue partagé.
      </p>

      {error && <div style={{ ...card, color: "#c0392b", fontSize: 13 }}>{error}</div>}

      {proposals === null && !error && (
        <div style={{ textAlign: "center", padding: 60, color: "#999", fontSize: 13 }}>Chargement...</div>
      )}

      {proposals && proposals.length === 0 && (
        <div style={{ ...card, textAlign: "center", color: "#999", fontSize: 13 }}>
          Aucune proposition en attente.
        </div>
      )}

      {proposals && proposals.map(p => {
        const isEditing = editingId === p.id;
        const isBusy = busyId === p.id;
        return (
          <div key={p.id} style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {p.user_avatar ? <img src={p.user_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(p.user_name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{p.user_name}</div>
                <div style={{ fontSize: 11.5, color: "#999" }}>{formatDate(p.created_at)}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: 0.5, color: "#666", backgroundColor: "#F0F0F0", borderRadius: 12, padding: "4px 10px", flexShrink: 0 }}>
                {p.type === "passion" ? "Passion" : "Power"}
              </span>
            </div>

            {!isEditing ? (
              <div style={{ backgroundColor: "#F8F8F8", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>{p.category || "Sans catégorie"}</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{p.label}</div>
              </div>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#666", display: "block", marginBottom: 4 }}>Catégorie</label>
                <input value={editCategory} onChange={e => setEditCategory(e.target.value)} style={input} placeholder="Catégorie" />
                <label style={{ fontSize: 11, fontWeight: 600, color: "#666", display: "block", marginBottom: 4 }}>Label</label>
                <input value={editLabel} onChange={e => setEditLabel(e.target.value)} style={{ ...input, marginBottom: 0 }} placeholder="Label" />
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
              {!isEditing ? (
                <>
                  <button disabled={isBusy} onClick={() => handleApprove(p.id)} style={primaryBtn}>
                    {isBusy ? "..." : "Accepter"}
                  </button>
                  <button disabled={isBusy} onClick={() => startEdit(p)} style={secondaryBtn}>
                    Modifier puis accepter
                  </button>
                  <button disabled={isBusy} onClick={() => handleReject(p.id)} style={dangerBtn}>
                    Refuser
                  </button>
                </>
              ) : (
                <>
                  <button
                    disabled={isBusy || !editLabel.trim()}
                    onClick={() => handleApprove(p.id, { label: editLabel.trim(), category: editCategory.trim() })}
                    style={primaryBtn}
                  >
                    {isBusy ? "..." : "Confirmer et accepter"}
                  </button>
                  <button disabled={isBusy} onClick={() => setEditingId(null)} style={secondaryBtn}>
                    Annuler
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
